import React, { useRef, useState, useEffect } from 'react';
import {
  X, Download, CheckCircle, XCircle, AlertTriangle, Clock, Camera,
  Car, User, FileText, Calendar, MapPin, Gauge, Timer, ClipboardCheck,
  AlertCircle, Printer, Eye, Film, Edit3, Save, Loader, Send, Shield, Mail,
  RefreshCw, Trash2, Bell
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../services/api';
import toast from 'react-hot-toast';
import PhotoEditorModal from './PhotoEditorModal';

/**
 * Constrói URL pública para foto de inspeção.
 * O backend armazena caminhos como /uploads/inspections/xxx.jpg
 * e os serve em /api/uploads/inspections/xxx.jpg (sem autenticação para /inspections/).
 */
const getPhotoSrc = (photo) => {
  const raw = photo?.photo_url || photo?.url;
  if (raw) {
    if (raw.startsWith('/uploads/')) return `/api${raw}`;
    if (raw.startsWith('uploads/')) return `/api/${raw}`;
    return raw;
  }
  if (photo?.filename) return `/api/uploads/inspections/${photo.filename}`;
  return '';
};

/**
 * Modal de Relatório de Inspeção - VIPLOC
 */
const InspectionReportModal = ({ inspection: initialInspection, onClose }) => {
  const reportRef = useRef(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [inspection, setInspection] = useState(initialInspection);

  // Edição de dados de entrega
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    condutor_nome: initialInspection?.condutor_nome || initialInspection?.driver_name || '',
    condutor_cpf: initialInspection?.condutor_cpf || '',
  });
  const [contractParties, setContractParties] = useState(null); // { locatarios: [], locadores: [] }

  // Contratos e ClickSign
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState(initialInspection?.contract_id || '');
  const [signatures, setSignatures] = useState([]);
  const [sendingToClicksign, setSendingToClicksign] = useState(false);
  const [cancellingClicksign, setCancellingClicksign] = useState(false);
  const [resendingSignerId, setResendingSignerId] = useState(null);
  const [syncingSignatures, setSyncingSignatures] = useState(false);

  // Seleção de signatários
  const [showSignerSelection, setShowSignerSelection] = useState(false);
  const [availableSigners, setAvailableSigners] = useState({ locatarios: [], locadores: [] });
  const [selectedSigners, setSelectedSigners] = useState([]);
  const [customSigner, setCustomSigner] = useState({ name: '', email: '', cpf: '', role: 'locatario' });
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [loadingSigners, setLoadingSigners] = useState(false);
  const [reportObservation, setReportObservation] = useState('');

  // Carregar contratos e assinaturas na montagem
  useEffect(() => {
    const loadData = async () => {
      try {
        // Carregar contratos
        const contractsRes = await api.get('/api/contracts');
        const allContracts = contractsRes.data?.data || contractsRes.data || [];
        const filtered = allContracts.filter(c =>
          ['negotiating', 'approved', 'active', 'sent_to_clicksign'].includes(c.status)
        );
        setContracts(filtered);
      } catch (err) {
        console.error('Erro ao carregar contratos:', err);
      }

      // Carregar e sincronizar assinaturas se já tiver ClickSign
      // O backend faz sync automático com a API do ClickSign
      if (initialInspection?.clicksign_document_key || initialInspection?.clicksign_status) {
        try {
          const sigRes = await api.get(`/api/inspections/${initialInspection.id}/signatures`);
          const sigs = sigRes.data?.data || [];
          setSignatures(sigs);
          // Atualizar status local se todas assinaram
          const allSigned = sigs.length > 0 && sigs.every(s => s.signed);
          if (allSigned && initialInspection.clicksign_status !== 'closed') {
            setInspection(prev => ({ ...prev, clicksign_status: 'closed' }));
          }
        } catch (err) {
          console.error('Erro ao carregar assinaturas:', err);
        }
      }
    };
    loadData();
  }, [initialInspection?.id, initialInspection?.clicksign_document_key]);

  // Ao selecionar um contrato, carregar partes signatárias + dados do termo de autorização
  const handleContractSelect = async (contractId) => {
    setSelectedContractId(contractId);
    if (!contractId) { setContractParties(null); return; }

    try {
      const res = await api.get(`/api/contracts/${contractId}`);
      const contract = res.data?.contract || res.data?.data || res.data;
      if (contract) {
        const locatarios = [];
        const tipoSig = contract.signatario_tipo || 'socio';
        if (tipoSig === 'procurador' && contract.locatario_representante_nome) {
          locatarios.push({ name: contract.locatario_representante_nome, cpf: contract.locatario_representante_cpf || '', email: contract.locatario_representante_email || '' });
        } else if (Array.isArray(contract.locatario_socios) && contract.locatario_socios.length > 0) {
          contract.locatario_socios.filter(s => s.assina !== false).forEach(s => {
            locatarios.push({ name: s.nome || s.name, cpf: s.cpf || s.document || '', email: s.email || '' });
          });
        } else {
          locatarios.push({ name: contract.client_name || '', cpf: contract.client_document || '', email: contract.client_email || '' });
        }

        const locadores = [];
        try {
          const partnerRes = await api.get('/api/company-partners/primary');
          const locador = partnerRes.data?.data;
          if (locador?.name) locadores.push({ name: locador.name, cpf: locador.cpf || '', email: locador.email || '' });
        } catch (_) {}

        setContractParties({ locatarios, locadores });

        // Buscar termo de autorização para auto-preencher condutor/recebedor
        try {
          const termRes = await api.get(`/api/authorization-terms/${contractId}`);
          const term = termRes.data?.term;
          if (term && term.representative_name) {
            const shouldFill = !inspection.condutor_nome && !inspection.recebedor_nome;
            if (shouldFill) {
              const newData = {
                condutor_nome: term.representative_name,
                condutor_cpf: term.representative_cpf || '',
                recebedor_nome: term.representative_name,
                recebedor_cpf: term.representative_cpf || '',
              };
              setDeliveryForm({ condutor_nome: newData.condutor_nome, condutor_cpf: newData.condutor_cpf });
              // Salvar automaticamente no backend
              await api.put(`/api/inspections/${inspection.id}/delivery-info`, newData);
              setInspection(prev => ({ ...prev, ...newData, authorization_term: term }));
              toast.success('Dados do representante preenchidos automaticamente do termo de entrega');
            } else {
              setInspection(prev => ({ ...prev, authorization_term: term }));
            }
          }
        } catch (_) {}

        toast.success('Partes do contrato carregadas');
      }
    } catch (err) {
      console.error('Erro ao buscar contrato:', err);
    }
  };

  // Carregar partes automaticamente se já tem contrato vinculado
  useEffect(() => {
    const contractId = inspection.contract_id || selectedContractId;
    if (contractId && !contractParties) {
      handleContractSelect(contractId);
    }
  }, [inspection.contract_id]);

  // Auto-preencher dados do termo de autorização (quando vem do backend)
  useEffect(() => {
    const term = initialInspection?.authorization_term;
    if (term && term.representative_name && !initialInspection?.condutor_nome && !initialInspection?.recebedor_nome) {
      setDeliveryForm({
        condutor_nome: term.representative_name,
        condutor_cpf: term.representative_cpf || '',
      });
      setInspection(prev => ({
        ...prev,
        condutor_nome: prev.condutor_nome || term.representative_name,
        condutor_cpf: prev.condutor_cpf || term.representative_cpf || '',
        recebedor_nome: prev.recebedor_nome || term.representative_name,
        recebedor_cpf: prev.recebedor_cpf || term.representative_cpf || '',
      }));
    }
  }, [initialInspection?.authorization_term]);

  // Sincronizar status manualmente
  const handleSyncSignatures = async () => {
    setSyncingSignatures(true);
    try {
      const res = await api.get(`/api/inspections/${inspection.id}/signatures`);
      const sigs = res.data?.data || [];
      setSignatures(sigs);
      const allSigned = sigs.length > 0 && sigs.every(s => s.signed);
      if (allSigned) setInspection(prev => ({ ...prev, clicksign_status: 'closed' }));
      toast.success('Status atualizado');
    } catch (err) {
      toast.error('Erro ao sincronizar assinaturas');
    } finally {
      setSyncingSignatures(false);
    }
  };

  // Cancelar documento ClickSign e permitir reenvio
  const handleCancelClicksign = async () => {
    if (!window.confirm('Cancelar o envio atual? Os signatários não poderão mais assinar este documento. Você poderá enviar um novo documento.')) return;
    setCancellingClicksign(true);
    try {
      await api.delete(`/api/inspections/${inspection.id}/cancel-clicksign`);
      setInspection(prev => ({ ...prev, clicksign_status: null, clicksign_document_key: null }));
      setSignatures([]);
      toast.success('Documento cancelado. Você pode enviar um novo documento para assinatura.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao cancelar documento');
    } finally {
      setCancellingClicksign(false);
    }
  };

  // Reenviar lembrete para signatário específico
  const handleResendSigner = async (sig) => {
    setResendingSignerId(sig.id);
    try {
      const res = await api.post(`/api/inspections/${inspection.id}/resend-signer/${sig.id}`);
      toast.success(res.data?.message || `Lembrete enviado para ${sig.signer_name}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao reenviar lembrete');
    } finally {
      setResendingSignerId(null);
    }
  };

  // Abrir seleção de signatários (passo 1)
  const handleOpenSignerSelection = async () => {
    setLoadingSigners(true);
    setShowSignerSelection(true);

    const locatarios = [];
    const locadores = [];

    // Buscar signatários do contrato vinculado
    const contractId = inspection.contract_id || selectedContractId;
    if (contractId) {
      try {
        const contRes = await api.get(`/api/contracts/${contractId}`);
        const contract = contRes.data?.contract || contRes.data?.data || contRes.data;
        if (contract) {
          const tipoSig = contract.signatario_tipo || 'socio';
          if (tipoSig === 'procurador' && contract.locatario_representante_nome) {
            locatarios.push({ name: contract.locatario_representante_nome, email: contract.locatario_representante_email || '', cpf: contract.locatario_representante_cpf || '', role: 'locatario', sign_as: 'party', source: 'Procurador' });
          } else if (Array.isArray(contract.locatario_socios) && contract.locatario_socios.length > 0) {
            contract.locatario_socios.forEach(s => {
              locatarios.push({ name: s.nome || s.name || '', email: s.email || '', cpf: s.cpf || s.document || '', role: 'locatario', sign_as: 'party', source: 'Sócio Locatário' });
            });
          } else if (contract.client_name) {
            locatarios.push({ name: contract.client_name, email: contract.client_email || '', cpf: contract.client_document || '', role: 'locatario', sign_as: 'party', source: 'Cliente' });
          }
        }
      } catch (err) {
        console.error('Erro ao buscar contrato:', err);
      }
    }

    // Buscar todos os sócios da empresa VIPLOC
    try {
      const partnerRes = await api.get('/api/company-partners');
      const partners = partnerRes.data?.data || partnerRes.data || [];
      partners.forEach(p => {
        if (p.name && p.email) {
          locadores.push({ name: p.name, email: p.email, cpf: p.cpf || '', role: 'locador', sign_as: 'party', source: p.role || 'Sócio VIPLOC', primary: p.is_primary });
        }
      });
    } catch (err) {
      // Fallback: buscar só o primário
      try {
        const partnerRes = await api.get('/api/company-partners/primary');
        const p = partnerRes.data?.data;
        if (p?.name && p?.email) {
          locadores.push({ name: p.name, email: p.email, cpf: p.cpf || '', role: 'locador', sign_as: 'party', source: 'Sócio Principal', primary: true });
        }
      } catch (_) {}
    }

    setAvailableSigners({ locatarios, locadores });
    // Pré-selecionar todos por padrão
    const allSigners = [...locatarios, ...locadores].map((s, i) => ({ ...s, _id: i }));
    setSelectedSigners(allSigners);
    setLoadingSigners(false);
  };

  // Alternar seleção de signatário
  const toggleSigner = (signer) => {
    setSelectedSigners(prev => {
      const exists = prev.find(s => s.email === signer.email);
      if (exists) return prev.filter(s => s.email !== signer.email);
      return [...prev, signer];
    });
  };

  // Adicionar signatário personalizado
  const handleAddCustomSigner = () => {
    if (!customSigner.name || !customSigner.email) {
      toast.error('Informe nome e email do signatário');
      return;
    }
    const newSigner = { ...customSigner, sign_as: 'party', source: 'Personalizado', _id: Date.now() };
    setSelectedSigners(prev => [...prev, newSigner]);
    setCustomSigner({ name: '', email: '', cpf: '', role: 'locatario' });
    setShowAddCustom(false);
    toast.success('Signatário adicionado');
  };

  // Enviar para ClickSign (passo 2 — após confirmar seleção)
  const handleSendToClicksign = async () => {
    if (selectedSigners.length === 0) {
      toast.error('Selecione pelo menos um signatário');
      return;
    }
    setSendingToClicksign(true);
    setShowSignerSelection(false);
    try {
      const pdf = await buildPdf();
      const pdfBase64 = pdf.output('datauristring').split(',')[1];

      const result = await api.post(`/api/inspections/${inspection.id}/send-to-clicksign`, {
        signers: selectedSigners.map(s => ({ name: s.name, email: s.email, cpf: s.cpf || '', role: s.role, sign_as: s.sign_as || 'party' })),
        pdfBase64
      });

      if (result.data?.success) {
        toast.success(`Enviado para assinatura! ${selectedSigners.length} signatário(s)`);
        setInspection(prev => ({ ...prev, clicksign_status: 'running', clicksign_document_key: result.data.data.documentKey }));
        const sigRes = await api.get(`/api/inspections/${inspection.id}/signatures`);
        setSignatures(sigRes.data?.data || []);
      } else {
        toast.error(result.data?.message || 'Erro ao enviar');
      }
    } catch (error) {
      console.error('Erro ao enviar para ClickSign:', error);
      toast.error('Erro ao enviar para ClickSign: ' + (error.response?.data?.message || error.message));
    } finally {
      setSendingToClicksign(false);
    }
  };

  // Formatar CPF
  const formatCpf = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, d) =>
        d ? `${a}.${b}.${c}-${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a
      );
    }
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, d, e) =>
      e ? `${a}.${b}.${c}/${d}-${e}` : d ? `${a}.${b}.${c}/${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a
    );
  };

  if (!inspection) return null;

  // Cores VIPLOC
  const colors = {
    navy: '#2B3E5C',
    navyDark: '#1a2537',
    orange: '#FF9933',
    orangeLight: '#FFA94D',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
  };

  // Agrupar itens por seção
  const groupedItems = inspection.items?.reduce((acc, item) => {
    const sectionName = item.section_name || 'Geral';
    if (!acc[sectionName]) {
      acc[sectionName] = [];
    }
    acc[sectionName].push(item);
    return acc;
  }, {}) || {};

  // Contar estatísticas - usar dados da view quando disponíveis
  // Se não há items mas há fotos, cada foto conta como um item inspecionado
  const itemsTotal = inspection.items_count || inspection.items?.length || 0;
  const photosTotal = inspection.photos_count || inspection.photos?.length || 0;
  const usePhotosAsItems = itemsTotal === 0 && photosTotal > 0;

  const stats = {
    total: usePhotosAsItems ? photosTotal : itemsTotal,
    passed: usePhotosAsItems
      ? (inspection.result === 'approved' ? photosTotal : 0)
      : ((inspection.items_count || 0) - (inspection.failed_items_count || 0) ||
         inspection.items?.filter(i => i.status === 'pass' || i.is_approved === true).length || 0),
    failed: usePhotosAsItems
      ? (inspection.result === 'rejected' ? photosTotal : (inspection.failed_items_count || 0))
      : (inspection.failed_items_count ||
         inspection.items?.filter(i => i.status === 'fail' || i.is_approved === false).length || 0),
    pending: usePhotosAsItems ? 0 : (inspection.items?.filter(i => i.status === 'pending' || i.is_approved === null).length || 0),
    photos: photosTotal,
    alerts: inspection.pending_alerts_count || inspection.alerts?.length || 0,
  };

  // Formatar data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatar data curta
  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  // Status config
  const getStatusConfig = (status) => {
    const configs = {
      scheduled: { label: 'Agendado', color: 'bg-blue-100 text-blue-800', icon: Clock },
      in_progress: { label: 'Em Andamento', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      completed: { label: 'Concluído', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      approved: { label: 'Aprovado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      requires_attention: { label: 'Requer Atenção', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
      failed: { label: 'Reprovado', color: 'bg-red-100 text-red-800', icon: XCircle },
      pending: { label: 'Pendente', color: 'bg-gray-100 text-gray-800', icon: Clock },
    };
    return configs[status] || configs.pending;
  };

  // Resultado config
  const getResultConfig = (result) => {
    const configs = {
      approved: { label: 'Aprovado', color: 'text-green-600', bgColor: 'bg-green-50', icon: CheckCircle },
      pass: { label: 'Aprovado', color: 'text-green-600', bgColor: 'bg-green-50', icon: CheckCircle },
      failed: { label: 'Reprovado', color: 'text-red-600', bgColor: 'bg-red-50', icon: XCircle },
      fail: { label: 'Reprovado', color: 'text-red-600', bgColor: 'bg-red-50', icon: XCircle },
      requires_attention: { label: 'Requer Atenção', color: 'text-orange-600', bgColor: 'bg-orange-50', icon: AlertTriangle },
    };
    return configs[result] || { label: result || 'Pendente', color: 'text-gray-600', bgColor: 'bg-gray-50', icon: Clock };
  };

  // Salvar dados de entrega (condutor)
  const handleSaveDelivery = async () => {
    setSavingDelivery(true);
    try {
      await api.put(`/api/inspections/${inspection.id}/delivery-info`, {
        condutor_nome: deliveryForm.condutor_nome,
        condutor_cpf: deliveryForm.condutor_cpf,
      });
      setInspection(prev => ({
        ...prev,
        condutor_nome: deliveryForm.condutor_nome,
        condutor_cpf: deliveryForm.condutor_cpf,
      }));
      setEditingDelivery(false);
      toast.success('Dados do condutor salvos!');
    } catch (error) {
      console.error('Erro ao salvar dados de entrega:', error);
      toast.error('Erro ao salvar. Tente novamente.');
    } finally {
      setSavingDelivery(false);
    }
  };

  // Callback quando foto é editada/recortada
  const handlePhotoUpdated = (updatedPhotoData) => {
    setInspection(prev => ({
      ...prev,
      photos: prev.photos?.map(p =>
        p.id === updatedPhotoData.id ? { ...p, ...updatedPhotoData } : p
      )
    }));
    setEditingPhoto(null);
  };

  // Encontrar ponto de corte seguro analisando pixels do canvas
  // Combina análise de pixels com zonas protegidas (data-no-break) do DOM
  // pageStart = Y onde começa a página atual (último corte)
  const findSafeCutPoint = (canvas, targetY, searchRange, noBreakZones, pageStart) => {
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const MIN_GAP = 8;
    const marginPx = Math.round(width * 0.08);
    const ZONE_PADDING = 5;
    const pageHeight = targetY - pageStart;
    const PAGE_TOLERANCE = Math.round(pageHeight * 0.05);

    const isInZone = y => noBreakZones.some(z => y > z.top - ZONE_PADDING && y < z.bottom + ZONE_PADDING);
    const getZone = y => noBreakZones.find(z => y > z.top - ZONE_PADDING && y < z.bottom + ZONE_PADDING) || null;

    const searchGap = (start, end) => {
      if (end <= start) return null;
      const data = ctx.getImageData(0, start, width, end - start);
      const pixels = data.data;
      const h = end - start;
      const rowWhite = new Uint8Array(h);
      for (let row = 0; row < h; row++) {
        rowWhite[row] = 1;
        const off = row * width * 4;
        for (let px = marginPx; px < width - marginPx; px += 15) {
          const i = off + px * 4;
          if (pixels[i] < 250 || pixels[i + 1] < 250 || pixels[i + 2] < 250) { rowWhite[row] = 0; break; }
        }
      }
      let best = null, bestDist = Infinity, gs = -1, gl = 0;
      for (let row = 0; row <= h; row++) {
        if (row < h && rowWhite[row]) { if (gs < 0) gs = row; gl++; }
        else {
          if (gl >= MIN_GAP) {
            const mid = start + gs + Math.floor(gl / 2);
            if (!isInZone(mid)) {
              const d = Math.abs(mid - targetY);
              if (d < bestDist) { bestDist = d; best = mid; }
            }
          }
          gs = -1; gl = 0;
        }
      }
      return best;
    };

    // Passo 1: busca gap branco na faixa normal (15% acima, 3.75% abaixo)
    const s1 = Math.max(0, targetY - searchRange);
    const e1 = Math.min(canvas.height, targetY + Math.round(searchRange * 0.25));
    let result = searchGap(s1, e1);
    if (result !== null) {
      const nextZone = noBreakZones.filter(z => z.top > result && z.top - result < searchRange * 0.5).sort((a, b) => a.top - b.top)[0];
      if (nextZone && nextZone.bottom - pageStart <= pageHeight) {
        let cut = nextZone.bottom + ZONE_PADDING;
        let loop = true;
        while (loop) {
          loop = false;
          const oz = getZone(cut);
          if (oz) {
            const nc = oz.bottom + ZONE_PADDING;
            if (nc - pageStart <= pageHeight) { cut = nc; loop = true; } else return result;
          }
        }
        const g = searchGap(cut, Math.min(canvas.height, cut + Math.round(searchRange * 0.3)));
        if (g !== null && g - pageStart <= pageHeight) return g;
        if (cut - pageStart <= pageHeight) return cut;
      }
      return result;
    }

    // Passo 2: targetY dentro de zona protegida → tentar estender
    const zone = getZone(targetY);
    if (zone) {
      let extendTo = zone.bottom + ZONE_PADDING;
      let found = true;
      while (found) {
        found = false;
        for (const z of noBreakZones) {
          if (z.top >= extendTo && z.top <= extendTo + searchRange && z.bottom + ZONE_PADDING - pageStart <= pageHeight) {
            extendTo = z.bottom + ZONE_PADDING; found = true;
          }
        }
      }
      // Tolerância 5%: cobre tanto o caso normal (≤ pageHeight) quanto 5% de overflow
      if (extendTo - pageStart <= pageHeight + PAGE_TOLERANCE) {
        return extendTo;
      }
      // Não cabe: recuar para antes da zona
      const prevZone = noBreakZones.filter(z => z.bottom < zone.top && z.top > pageStart).sort((a, b) => b.bottom - a.bottom)[0];
      if (prevZone) {
        const cutBefore = zone.top - ZONE_PADDING;
        if (cutBefore > pageStart + pageHeight * 0.3) return cutBefore;
      }
      const beforeZone = zone.top - ZONE_PADDING;
      if (beforeZone > pageStart + pageHeight * 0.3) return beforeZone;
    }

    // Passo 3: busca em toda a página
    const r3 = searchGap(Math.max(0, pageStart + 50), s1);
    if (r3 !== null) return r3;

    // Passo 4: cortar antes da zona
    if (zone) { const sc = zone.top - ZONE_PADDING; if (sc > pageStart + 50) return sc; }

    return targetY;
  };

  // Constrói o objeto jsPDF com a lógica validada (corte inteligente por pixels).
  // Usado tanto pelo botão "Salvar PDF" quanto pelo envio ao ClickSign.
  const buildPdf = async () => {
    const element = reportRef.current;
    if (!element) throw new Error('Referência do relatório não encontrada');

      // Constantes A4
      const A4_WIDTH_MM = 210;
      const MARGIN_MM = 6;
      const USABLE_WIDTH_MM = A4_WIDTH_MM - (MARGIN_MM * 2); // 198mm
      const USABLE_HEIGHT_MM = 297 - (MARGIN_MM * 2); // 285mm

      // Ocultar elementos não-PDF e trocar screen-only/pdf-only
      const noPrintElements = element.querySelectorAll('[data-no-print]');
      const screenOnlyElements = element.querySelectorAll('[data-screen-only]');
      const pdfOnlyElements = element.querySelectorAll('[data-pdf-only]');
      const pdfTextLinkElements = element.querySelectorAll('[data-pdf-link-text]');
      noPrintElements.forEach(el => { el.style.display = 'none'; });
      screenOnlyElements.forEach(el => { el.style.display = 'none'; });
      pdfOnlyElements.forEach(el => { el.style.display = 'block'; });

      // data-pdf-link-text: tornar visíveis como links azuis no canvas
      // (igual ao script de teste validado — mais fiel ao layout real)
      pdfTextLinkElements.forEach(el => {
        el.style.display = 'block';
        el.style.visibility = 'visible';
        el.style.color = '#0066CC';
        el.style.textDecoration = 'underline';
        el.style.fontSize = '13px';
      });

      // Remover borda/padding do wrapper de itens — no PDF as seções aparecem sem a caixa externa
      const itemsWrapper = element.querySelector('.rounded-xl.border.border-gray-200.p-6.mb-6');
      const itemsWrapperOrigStyle = itemsWrapper ? itemsWrapper.getAttribute('style') || '' : null;
      if (itemsWrapper) {
        itemsWrapper.style.border = 'none';
        itemsWrapper.style.borderRadius = '0';
        itemsWrapper.style.padding = '0';
      }

      // Esperar layout recalcular após mudanças de display
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 150))));

      // ─────────────────────────────────────────────────────────
      // PRÉ-CARREGAR todas as imagens como dataURLs antes do html2canvas.
      // Usa concorrência limitada (6 por vez) para não disparar rate limit.
      // Imagens já em dataURL (assinaturas base64) são puladas.
      // ─────────────────────────────────────────────────────────
      const origSrcs = new Map();
      const imgs = Array.from(element.querySelectorAll('img')).filter(img => {
        const src = img.getAttribute('src') || '';
        return src && !src.startsWith('data:');
      });

      const fetchToDataUrl = async (img) => {
        const src = img.getAttribute('src');
        origSrcs.set(img, src);
        try {
          const resp = await fetch(src, { credentials: 'same-origin', cache: 'force-cache' });
          if (!resp.ok) return;
          const blob = await resp.blob();
          const dataUrl = await new Promise(res => {
            const reader = new FileReader();
            reader.onloadend = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
          img.src = dataUrl;
        } catch (_) {
          origSrcs.delete(img);
        }
      };

      // Processar em lotes de 6 para não saturar o servidor
      const BATCH = 6;
      for (let i = 0; i < imgs.length; i += BATCH) {
        await Promise.all(imgs.slice(i, i + BATCH).map(fetchToDataUrl));
      }

      // Aguardar as imagens renderizarem com os novos src
      await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 100)));

      // Coletar zonas protegidas (data-no-break) usando getBoundingClientRect
      // Reset scroll para medições consistentes
      const savedScrollTop = element.scrollTop;
      element.scrollTop = 0;
      // Aguardar layout estabilizar após scroll reset
      await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 50)));

      // Coletar apenas zonas de seção — excluir itens individuais de checklist
      // (rounded-lg p-4 border-l-4 têm fundo colorido sem gaps brancos entre eles,
      // o que impedia o algoritmo de encontrar pontos de corte seguros)
      const containerRect = element.getBoundingClientRect();
      const noBreakDomZones = Array.from(element.querySelectorAll('[data-no-break="true"]'))
        .filter(el => !(el.classList.contains('rounded-lg') && el.classList.contains('p-4') && el.classList.contains('border-l-4')))
        .map(el => {
          const rect = el.getBoundingClientRect();
          const buffer = parseInt(el.getAttribute('data-no-break-buffer') || '0');
          return {
            top: rect.top - containerRect.top + element.scrollTop,
            bottom: rect.bottom - containerRect.top + element.scrollTop,
            buffer
          };
        });
      // Medir posição dos links de texto PDF (data-pdf-link-text)
      const containerLeft = containerRect.left;
      const pdfTextLinks = Array.from(element.querySelectorAll('[data-pdf-link-text]')).map(el => {
        const rect = el.getBoundingClientRect();
        return {
          top: rect.top - containerRect.top + element.scrollTop,
          bottom: rect.bottom - containerRect.top + element.scrollTop,
          left: rect.left - containerLeft,  // X relativo ao container (px DOM)
          url: el.getAttribute('data-pdf-link-text'),
          text: el.textContent.trim(),
        };
      });

      // Restaurar scroll
      element.scrollTop = savedScrollTop;

      // Capturar canvas completo
      const scale = 1.5;
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        imageTimeout: 30000
      });

      // Restaurar visibilidade e srcs originais das imagens
      noPrintElements.forEach(el => { el.style.display = ''; });
      screenOnlyElements.forEach(el => { el.style.display = ''; });
      pdfOnlyElements.forEach(el => { el.style.display = 'none'; });
      pdfTextLinkElements.forEach(el => { el.style.display = ''; el.style.visibility = ''; el.style.color = ''; el.style.textDecoration = ''; el.style.fontSize = ''; });
      if (itemsWrapper) { itemsWrapper.setAttribute('style', itemsWrapperOrigStyle); }
      origSrcs.forEach((src, img) => { img.src = src; });

      // Criar PDF A4
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 2 * MARGIN_MM;
      const mmPerCanvasPx = imgWidth / canvas.width;
      const canvasPixelsPerPage = Math.floor(USABLE_HEIGHT_MM / mmPerCanvasPx);

      // Buscar ~15% da altura da página para encontrar gaps
      const searchRange = Math.round(canvasPixelsPerPage * 0.15);

      // Converter zonas protegidas para coordenadas de canvas (DOM px * scale)
      // buffer: margem extra por zona para cobrir discrepância de medição (0 para a maioria, 40 para Observação Adicional)
      const noBreakZones = noBreakDomZones.map(z => ({
        top: Math.max(0, Math.round(z.top * scale) - z.buffer),
        bottom: Math.round(z.bottom * scale) + z.buffer,
      }));

      // Encontrar pontos de corte seguros (linhas brancas entre seções)
      const cutPoints = [0];
      let currentY = 0;

      while (currentY + canvasPixelsPerPage < canvas.height) {
        const targetCut = currentY + canvasPixelsPerPage;
        const safeCut = findSafeCutPoint(canvas, targetCut, searchRange, noBreakZones, currentY);
        cutPoints.push(safeCut);
        currentY = safeCut;
      }
      cutPoints.push(canvas.height);

      // Eliminar última página quase vazia: se o conteúdo restante é < 15% da página
      // E o conteúdo mesclado cabe no A4, mesclar com a página anterior
      if (cutPoints.length > 2) {
        const lastPageHeight = cutPoints[cutPoints.length - 1] - cutPoints[cutPoints.length - 2];
        const prevPageHeight = cutPoints[cutPoints.length - 2] - cutPoints[cutPoints.length - 3];
        if (lastPageHeight < canvasPixelsPerPage * 0.15 &&
            prevPageHeight + lastPageHeight <= canvasPixelsPerPage) {
          cutPoints.splice(cutPoints.length - 2, 1);
        }
      }

      // Fatiar canvas nos pontos seguros
      for (let i = 0; i < cutPoints.length - 1; i++) {
        if (i > 0) pdf.addPage();

        const sourceY = cutPoints[i];
        const sourceHeight = cutPoints[i + 1] - sourceY;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.80);
        const naturalH = sourceHeight * mmPerCanvasPx;
        const SAFE_MAX_H = 297 - MARGIN_MM - 5; // 282mm — margem inferior de 5mm

        if (naturalH <= SAFE_MAX_H) {
          pdf.addImage(pageImgData, 'JPEG', MARGIN_MM, MARGIN_MM, imgWidth, naturalH, undefined, 'FAST');
        } else {
          // Página levemente maior por tolerância: escalar proporcionalmente para caber no A4
          const ratio = SAFE_MAX_H / naturalH;
          const scaledW = imgWidth * ratio;
          const xOff = MARGIN_MM + (imgWidth - scaledW) / 2;
          pdf.addImage(pageImgData, 'JPEG', xOff, MARGIN_MM, scaledW, SAFE_MAX_H, undefined, 'FAST');
        }
      }

      // Adicionar links de texto clicáveis (data-pdf-link-text) como texto PDF real
      // Isso garante que são clicáveis em qualquer visualizador PDF
      for (const linkInfo of pdfTextLinks) {
        if (!linkInfo.url || !linkInfo.text) continue;
        const pUrl = linkInfo.url.startsWith('http') ? linkInfo.url : `https://gestor.viploclocadora.com.br${linkInfo.url}`;
        const topCanvas = Math.round(linkInfo.top * scale);
        const bottomCanvas = Math.round(linkInfo.bottom * scale);

        for (let i = 0; i < cutPoints.length - 1; i++) {
          if (topCanvas >= cutPoints[i] && topCanvas < cutPoints[i + 1]) {
            // Centro vertical do elemento no PDF
            const yTop = (topCanvas - cutPoints[i]) * mmPerCanvasPx + MARGIN_MM;
            const yBottom = (bottomCanvas - cutPoints[i]) * mmPerCanvasPx + MARGIN_MM;
            const yCenter = (yTop + yBottom) / 2;

            // X: converter posição DOM para mm no PDF
            const xMm = linkInfo.left * mmPerCanvasPx * scale + MARGIN_MM;
            const yText = yTop + (yBottom - yTop) * 0.7; // baseline ~70% da altura do elemento

            pdf.setPage(i + 1);
            pdf.setFontSize(10);
            pdf.setTextColor(0, 102, 204);   // azul #0066CC
            pdf.setFont('helvetica', 'normal');
            // textWithLink renderiza texto visível + cria anotação de link clicável
            pdf.textWithLink(linkInfo.text, xMm, yText, { url: pUrl });
            pdf.setTextColor(0, 0, 0);        // resetar cor
            break;
          }
        }
      }

      // Rodapé com número de página em cada página
      const totalPages = cutPoints.length - 1;
      for (let i = 0; i < totalPages; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(160, 160, 160);
        pdf.text(
          `Inspeção #${inspection.id}  |  Página ${i + 1} de ${totalPages}  |  VIPLOC`,
          pdfWidth / 2,
          pdfHeight - 5,
          { align: 'center' }
        );
      }

      return pdf;
  };

  // Gerar PDF com corte inteligente por análise de pixels
  const generatePDF = async () => {
    if (!reportRef.current) return;
    setGeneratingPdf(true);
    try {
      const pdf = await buildPdf();
      const fileName = `Inspecao_${inspection.id}_${formatDateShort(inspection.scheduled_date || inspection.created_at).replace(/\//g, '-')}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Renderizar resposta do item
  const renderItemResponse = (item) => {
    const isApproved = item.status === 'pass' || item.is_approved === true;
    const isFailed = item.status === 'fail' || item.is_approved === false;

    if (isApproved) {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">OK / Aprovado</span>
        </div>
      );
    }

    if (isFailed) {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Falha / Reprovado</span>
        </div>
      );
    }

    if (item.response_value) {
      return (
        <div className="flex items-center gap-2 text-gray-700">
          <span className="font-medium">{item.response_value}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 text-gray-400">
        <Clock className="w-5 h-5" />
        <span>Não inspecionado</span>
      </div>
    );
  };

  const statusConfig = getStatusConfig(inspection.status);
  const resultConfig = getResultConfig(inspection.result);
  const StatusIcon = statusConfig.icon;
  const ResultIcon = resultConfig.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full my-8">

        {/* Header do Modal - Fixo */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 rounded-t-xl"
          style={{ backgroundColor: colors.navy }}
        >
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-xl font-bold text-white">Relatório de Inspeção</h2>
              <p className="text-sm text-gray-300">#{inspection.id}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
              style={{
                backgroundColor: colors.orange,
                color: 'white',
                opacity: generatingPdf ? 0.7 : 1
              }}
            >
              {generatingPdf ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Salvar PDF
                </>
              )}
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Conteúdo do Relatório */}
        <div ref={reportRef} className="p-4 bg-white">

          {/* Cabeçalho do Relatório */}
          <div
            className="rounded-xl p-6 mb-6"
            style={{ backgroundColor: colors.navy }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Logo VIPLOC */}
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.orange }}
                >
                  <span className="text-white font-bold text-xl">VIP</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">VIPLOC</h1>
                  <p className="text-gray-300 text-sm">Gestão de Locação de Veículos</p>
                </div>
              </div>

              <div className="text-right">
                <h2 className="text-xl font-bold text-white">RELATÓRIO DE INSPEÇÃO</h2>
                <p className="text-gray-300">Inspeção #{inspection.id}</p>
                <p className="text-gray-300 text-sm mt-1">
                  Emitido em: {formatDate(new Date())}
                </p>
              </div>
            </div>
          </div>

          {/* Status e Resultado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div
              className="rounded-xl p-4 border-l-4"
              style={{
                backgroundColor: statusConfig.color.includes('green') ? '#f0fdf4' :
                                 statusConfig.color.includes('yellow') ? '#fefce8' :
                                 statusConfig.color.includes('red') ? '#fef2f2' :
                                 statusConfig.color.includes('blue') ? '#eff6ff' : '#f9fafb',
                borderLeftColor: statusConfig.color.includes('green') ? colors.success :
                                  statusConfig.color.includes('yellow') ? colors.warning :
                                  statusConfig.color.includes('red') ? colors.error :
                                  statusConfig.color.includes('blue') ? '#3b82f6' : '#6b7280'
              }}
            >
              <p className="text-sm text-gray-600 mb-1">Status da Inspeção</p>
              <div className="flex items-center gap-2">
                <StatusIcon className="w-6 h-6" style={{
                  color: statusConfig.color.includes('green') ? colors.success :
                         statusConfig.color.includes('yellow') ? colors.warning :
                         statusConfig.color.includes('red') ? colors.error :
                         statusConfig.color.includes('blue') ? '#3b82f6' : '#6b7280'
                }} />
                <span className="text-xl font-bold" style={{ color: colors.navy }}>
                  {statusConfig.label}
                </span>
              </div>
            </div>

            {inspection.result && (
              <div
                className={`rounded-xl p-4 border-l-4 ${resultConfig.bgColor}`}
                style={{
                  borderLeftColor: resultConfig.color.includes('green') ? colors.success :
                                   resultConfig.color.includes('orange') ? colors.warning :
                                   resultConfig.color.includes('red') ? colors.error : '#6b7280'
                }}
              >
                <p className="text-sm text-gray-600 mb-1">Resultado Final</p>
                <div className="flex items-center gap-2">
                  <ResultIcon className={`w-6 h-6 ${resultConfig.color}`} />
                  <span className={`text-xl font-bold`} style={{ color: colors.navy }}>
                    {resultConfig.label}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Informações Gerais */}
          <div data-no-break="true" className="rounded-xl border border-gray-200 p-6 mb-6">
            <h3
              className="text-lg font-bold mb-4 flex items-center gap-2"
              style={{ color: colors.navy }}
            >
              <FileText className="w-5 h-5" style={{ color: colors.orange }} />
              Informações Gerais
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Template */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Tipo de Inspeção</p>
                <p className="font-semibold" style={{ color: colors.navy }}>
                  {inspection.template_name || inspection.template?.name || 'N/A'}
                </p>
              </div>

              {/* Data */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Data da Inspeção</p>
                <p className="font-semibold flex items-center gap-1" style={{ color: colors.navy }}>
                  <Calendar className="w-4 h-4" style={{ color: colors.orange }} />
                  {formatDateShort(inspection.scheduled_date || inspection.created_at)}
                </p>
              </div>

              {/* Execução */}
              {inspection.executed_at && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Executado em</p>
                  <p className="font-semibold" style={{ color: colors.navy }}>
                    {formatDate(inspection.executed_at)}
                  </p>
                </div>
              )}

              {/* Contrato */}
              {inspection.contract_number && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Contrato</p>
                  <p className="font-semibold" style={{ color: colors.navy }}>
                    #{inspection.contract_number}
                  </p>
                </div>
              )}
            </div>

            <div className="h-px bg-gray-200 my-4"></div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Veículo */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Veículo</p>
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5" style={{ color: colors.orange }} />
                  <div>
                    <p className="font-semibold" style={{ color: colors.navy }}>
                      {inspection.vehicle_plate || inspection.vehicle_license_plate || inspection.license_plate || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {inspection.vehicle_model || ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Motorista */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Motorista</p>
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" style={{ color: colors.orange }} />
                  <p className="font-semibold" style={{ color: colors.navy }}>
                    {inspection.driver_name || 'Não informado'}
                  </p>
                </div>
              </div>

              {/* Odômetro */}
              {inspection.odometer_reading && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Odômetro</p>
                  <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5" style={{ color: colors.orange }} />
                    <p className="font-semibold" style={{ color: colors.navy }}>
                      {Number(inspection.odometer_reading).toLocaleString('pt-BR')} km
                    </p>
                  </div>
                </div>
              )}

              {/* Horímetro */}
              {inspection.engine_hours && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Horímetro</p>
                  <div className="flex items-center gap-2">
                    <Timer className="w-5 h-5" style={{ color: colors.orange }} />
                    <p className="font-semibold" style={{ color: colors.navy }}>
                      {Number(inspection.engine_hours).toLocaleString('pt-BR')} h
                    </p>
                  </div>
                </div>
              )}

              {/* Localização */}
              {inspection.location && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Local</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" style={{ color: colors.orange }} />
                    <p className="font-semibold" style={{ color: colors.navy }}>
                      {inspection.location}
                    </p>
                  </div>
                </div>
              )}

              {/* Geolocalização GPS */}
              {(inspection.latitude && inspection.longitude) && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Geolocalização GPS</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" style={{ color: '#10b981' }} />
                    <div>
                      <p className="font-semibold text-sm" style={{ color: colors.navy }}>
                        {Number(inspection.latitude).toFixed(6)}, {Number(inspection.longitude).toFixed(6)}
                      </p>
                      <a
                        href={`https://www.google.com/maps?q=${inspection.latitude},${inspection.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                        data-no-print="true"
                      >
                        Ver no Google Maps
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Estatísticas */}
          <div data-no-print="true" className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#f0fdf4' }}>
              <p className="text-3xl font-bold" style={{ color: colors.success }}>{stats.passed}</p>
              <p className="text-sm text-gray-600">Aprovados</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#fef2f2' }}>
              <p className="text-3xl font-bold" style={{ color: colors.error }}>{stats.failed}</p>
              <p className="text-sm text-gray-600">Reprovados</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#f9fafb' }}>
              <p className="text-3xl font-bold" style={{ color: colors.navy }}>{stats.total}</p>
              <p className="text-sm text-gray-600">Total Itens</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#eff6ff' }}>
              <p className="text-3xl font-bold" style={{ color: '#3b82f6' }}>{stats.photos}</p>
              <p className="text-sm text-gray-600">Fotos</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#fefce8' }}>
              <p className="text-3xl font-bold" style={{ color: colors.warning }}>{stats.alerts}</p>
              <p className="text-sm text-gray-600">Alertas</p>
            </div>
          </div>

          {/* Itens da Inspeção */}
          {Object.keys(groupedItems).length > 0 && (
            <div className="rounded-xl border border-gray-200 p-6 mb-6">
              <h3
                data-no-print="true"
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <ClipboardCheck className="w-5 h-5" style={{ color: colors.orange }} />
                Itens Inspecionados
              </h3>

              <div className="space-y-6">
                {Object.entries(groupedItems).map(([sectionName, items], sectionIndex) => (
                  <div key={sectionIndex}>
                    {/* Cabeçalho da Seção */}
                    <div
                      data-no-break="true"
                      className="rounded-lg px-4 py-2 mb-3"
                      style={{ backgroundColor: colors.navy }}
                    >
                      <h4 className="font-semibold text-white">
                        {sectionName}
                      </h4>
                    </div>

                    {/* Itens da Seção */}
                    <div className="space-y-3">
                      {items.map((item, itemIndex) => {
                        const isApproved = item.status === 'pass' || item.is_approved === true;
                        const isFailed = item.status === 'fail' || item.is_approved === false;

                        return (
                          <div
                            key={itemIndex}
                            className={`rounded-lg p-4 border-l-4 ${
                              isApproved ? 'bg-green-50 border-green-500' :
                              isFailed ? 'bg-red-50 border-red-500' :
                              'bg-gray-50 border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium" style={{ color: colors.navy }}>
                                  {item.item_name || item.name}
                                </p>
                                {item.item_description && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    {item.item_description}
                                  </p>
                                )}
                              </div>
                              <div className="ml-4">
                                {renderItemResponse(item)}
                              </div>
                            </div>

                            {/* Comentários */}
                            {item.comments && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Observações:</span> {item.comments}
                                </p>
                              </div>
                            )}

                            {/* Valor numérico ou texto */}
                            {item.response_value && item.field_type !== 'pass_fail' && item.field_type !== 'yes_no' && item.field_type !== 'ok_fail' && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Valor registrado:</span> {item.response_value}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fotos da Inspeção - Agrupadas por Tipo */}
          {inspection.photos && inspection.photos.length > 0 && (
            <div className="rounded-xl border border-gray-200 p-6 mb-6">
              <h3
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <Camera className="w-5 h-5" style={{ color: colors.orange }} />
                Registro Fotográfico ({inspection.photos.length} fotos)
              </h3>

              {/* Agrupar fotos por tipo */}
              {(() => {
                // Traduzir tipos de foto
                const getPhotoTypeLabel = (type) => {
                  const types = {
                    'odometer': 'Hodômetro/Medidor',
                    'meter': 'Medidor',
                    'item_photo': 'Itens Inspecionados',
                    'damage': 'Danos Identificados',
                    'before': 'Antes do Reparo',
                    'after': 'Após o Reparo',
                    'signature': 'Assinaturas',
                    'general': 'Fotos Gerais'
                  };
                  return types[type] || 'Outras Fotos';
                };

                // Separar fotos do hodômetro/CNH (tipo odometer) para exibição especial
                const odometerPhotos = inspection.photos.filter(p => p.photo_type === 'odometer');
                // Fotos adicionais (livres, com título customizado)
                const additionalPhotos = inspection.photos.filter(p => p.photo_type === 'additional');
                const otherPhotos = inspection.photos.filter(p => p.photo_type !== 'odometer' && p.photo_type !== 'additional');

                // Separar fotos com item e fotos gerais (excluindo odometer e additional)
                const photosWithItem = otherPhotos.filter(p => p.item_name);
                const photosWithoutItem = otherPhotos.filter(p => !p.item_name);

                // Agrupar fotos sem item por tipo
                const groupedByType = photosWithoutItem.reduce((acc, photo) => {
                  const type = photo.photo_type || 'general';
                  if (!acc[type]) acc[type] = [];
                  acc[type].push(photo);
                  return acc;
                }, {});

                // Agrupar fotos com item por seção
                const groupedBySection = photosWithItem.reduce((acc, photo) => {
                  const sectionName = photo.section_name || 'Itens';
                  if (!acc[sectionName]) acc[sectionName] = [];
                  acc[sectionName].push(photo);
                  return acc;
                }, {});

                return (
                  <div className="space-y-6">
                    {/* Hodômetro e CNH - Seção em Destaque */}
                    {odometerPhotos.length > 0 && (
                      <div>
                        <div
                          className="rounded-lg px-3 py-2 mb-3"
                          style={{ backgroundColor: colors.navy }}
                        >
                          <h4 className="font-semibold text-white flex items-center gap-2">
                            <Gauge className="w-4 h-4" />
                            Hodômetro / CNH do Condutor
                          </h4>
                        </div>

                        <div data-photo-row="true" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {odometerPhotos.map((photo, index) => (
                            <div
                              key={`odometer-${index}`}
                              className="rounded-lg border-2 border-blue-400 overflow-hidden"
                            >
                              <div className="px-3 py-2 bg-blue-50">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-semibold text-sm" style={{ color: colors.navy }}>
                                      {photo.caption || 'Hodômetro'}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    <Gauge className="w-3 h-3" />
                                    Registro
                                  </div>
                                </div>
                              </div>
                              <div
                                className="relative cursor-pointer group"
                                onClick={() => setSelectedPhoto(photo)}
                              >
                                <img
                                  src={getPhotoSrc(photo)}
                                  alt={photo.caption || `Foto ${index + 1}`}
                                  className="w-full object-cover transition-transform group-hover:scale-105"
                                  style={{ height: '220px' }}
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+U2VtIGltYWdlbTwvdGV4dD48L3N2Zz4=';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center gap-3">
                                  <Eye className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  <button
                                    data-no-print="true"
                                    onClick={(e) => { e.stopPropagation(); setEditingPhoto(photo); }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2 hover:bg-opacity-100 shadow-lg"
                                    title="Editar/Recortar foto"
                                  >
                                    <Edit3 className="w-4 h-4 text-gray-700" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fotos agrupadas por seção (quando tem item associado) */}
                    {Object.entries(groupedBySection).map(([sectionName, photos], sectionIndex) => (
                      <div key={`section-${sectionIndex}`}>
                        <div
                          className="rounded-lg px-3 py-2 mb-3"
                          style={{ backgroundColor: colors.navy }}
                        >
                          <h4 className="font-semibold text-white flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            {sectionName}
                          </h4>
                        </div>

                        <div className="space-y-4">
                          {/* Agrupar fotos em linhas de 2 para controle de quebra de página */}
                          {(() => {
                            const rows = [];
                            for (let i = 0; i < photos.length; i += 2) {
                              rows.push(photos.slice(i, i + 2));
                            }
                            return rows.map((rowPhotos, rowIndex) => (
                              <div key={rowIndex} data-photo-row="true" className={`grid grid-cols-1 ${rowPhotos.length === 2 ? 'md:grid-cols-2' : ''} gap-4`}>
                                {rowPhotos.map((photo, photoIndex) => {
                                  const isApproved = photo.item_status === 'pass' || photo.item_approved === true;
                                  const isFailed = photo.item_status === 'fail' || photo.item_approved === false;

                                  return (
                                    <div
                                      key={photoIndex}
                                      className={`rounded-lg border-2 overflow-hidden ${
                                        isApproved ? 'border-green-400' :
                                        isFailed ? 'border-red-400' :
                                        'border-gray-300'
                                      }`}
                                    >
                                      <div className={`px-3 py-2 ${
                                        isApproved ? 'bg-green-50' :
                                        isFailed ? 'bg-red-50' :
                                        'bg-gray-50'
                                      }`}>
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <p className="font-semibold text-sm" style={{ color: colors.navy }}>
                                              {photo.item_name}
                                            </p>
                                            {photo.caption && (
                                              <p className="text-xs text-gray-600 mt-1">
                                                {photo.caption}
                                              </p>
                                            )}
                                          </div>
                                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                            isApproved ? 'bg-green-100 text-green-700' :
                                            isFailed ? 'bg-red-100 text-red-700' :
                                            'bg-gray-100 text-gray-700'
                                          }`}>
                                            {isApproved ? (
                                              <><CheckCircle className="w-3 h-3" /> OK</>
                                            ) : isFailed ? (
                                              <><XCircle className="w-3 h-3" /> Falha</>
                                            ) : (
                                              <><Clock className="w-3 h-3" /> Pendente</>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      <div
                                        className="relative cursor-pointer group"
                                        onClick={() => setSelectedPhoto(photo)}
                                      >
                                        <img
                                          src={getPhotoSrc(photo)}
                                          alt={photo.caption || photo.item_name || `Foto ${photoIndex + 1}`}
                                          className="w-full object-cover transition-transform group-hover:scale-105"
                                          style={{ height: '220px' }}
                                          onError={(e) => {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+U2VtIGltYWdlbTwvdGV4dD48L3N2Zz4=';
                                          }}
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center gap-3">
                                          <Eye className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                          <button
                                            data-no-print="true"
                                            onClick={(e) => { e.stopPropagation(); setEditingPhoto(photo); }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2 hover:bg-opacity-100 shadow-lg"
                                            title="Editar/Recortar foto"
                                          >
                                            <Edit3 className="w-4 h-4 text-gray-700" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    ))}

                    {/* Fotos Adicionais do Vistoriador */}
                    {additionalPhotos.length > 0 && (
                      <div>
                        <div
                          className="rounded-lg px-3 py-2 mb-3"
                          style={{ backgroundColor: '#7e22ce' }}
                        >
                          <h4 className="font-semibold text-white flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            Fotos Adicionais do Vistoriador ({additionalPhotos.length})
                          </h4>
                        </div>

                        <div className="space-y-4">
                          {(() => {
                            const rows = [];
                            for (let i = 0; i < additionalPhotos.length; i += 2) {
                              rows.push(additionalPhotos.slice(i, i + 2));
                            }
                            return rows.map((rowPhotos, rowIndex) => (
                              <div key={rowIndex} data-photo-row="true" className={`grid grid-cols-1 ${rowPhotos.length === 2 ? 'md:grid-cols-2' : ''} gap-4`}>
                                {rowPhotos.map((photo, index) => (
                                  <div
                                    key={index}
                                    className="rounded-lg overflow-hidden"
                                    style={{ border: '2px solid #a855f7' }}
                                  >
                                    <div className="px-3 py-2" style={{ backgroundColor: '#faf5ff' }}>
                                      <p className="font-semibold text-sm" style={{ color: '#7e22ce' }}>
                                        {photo.caption || `Foto adicional ${index + 1}`}
                                      </p>
                                    </div>
                                    <div
                                      className="relative cursor-pointer group"
                                      onClick={() => setSelectedPhoto(photo)}
                                    >
                                      <img
                                        src={getPhotoSrc(photo)}
                                        alt={photo.caption || `Foto adicional ${index + 1}`}
                                        className="w-full object-cover transition-transform group-hover:scale-105"
                                        style={{ height: '220px' }}
                                        onError={(e) => {
                                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+U2VtIGltYWdlbTwvdGV4dD48L3N2Zz4=';
                                        }}
                                      />
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center gap-3">
                                        <Eye className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <button
                                          data-no-print="true"
                                          onClick={(e) => { e.stopPropagation(); setEditingPhoto(photo); }}
                                          className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2 hover:bg-opacity-100 shadow-lg"
                                          title="Editar/Recortar foto"
                                        >
                                          <Edit3 className="w-4 h-4 text-gray-700" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Fotos agrupadas por tipo (quando não tem item associado) */}
                    {Object.entries(groupedByType).map(([photoType, photos], typeIndex) => (
                      <div key={`type-${typeIndex}`}>
                        <div
                          className="rounded-lg px-3 py-2 mb-3"
                          style={{ backgroundColor: colors.navy }}
                        >
                          <h4 className="font-semibold text-white flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            {getPhotoTypeLabel(photoType)} ({photos.length})
                          </h4>
                        </div>

                        <div className="space-y-4">
                          {/* Agrupar fotos em linhas de 2 */}
                          {(() => {
                            const rows = [];
                            for (let i = 0; i < photos.length; i += 2) {
                              rows.push(photos.slice(i, i + 2));
                            }
                            return rows.map((rowPhotos, rowIndex) => (
                              <div key={rowIndex} data-photo-row="true" className={`grid grid-cols-1 ${rowPhotos.length === 2 ? 'md:grid-cols-2' : ''} gap-4`}>
                                {rowPhotos.map((photo, index) => (
                                  <div
                                    key={index}
                                    className="rounded-lg border-2 border-gray-300 overflow-hidden"
                                  >
                                    {/* Header com informações */}
                                    <div className="px-3 py-2 bg-gray-50">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <p className="font-semibold text-sm" style={{ color: colors.navy }}>
                                            {photo.caption || getPhotoTypeLabel(photoType)}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                          <Camera className="w-3 h-3" />
                                          Registro
                                        </div>
                                      </div>
                                    </div>
                                    {/* Imagem */}
                                    <div
                                      className="relative cursor-pointer group"
                                      onClick={() => setSelectedPhoto(photo)}
                                    >
                                      <img
                                        src={getPhotoSrc(photo)}
                                        alt={photo.caption || `Foto ${index + 1}`}
                                        className="w-full object-cover transition-transform group-hover:scale-105"
                                        style={{ height: '220px' }}
                                        onError={(e) => {
                                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+U2VtIGltYWdlbTwvdGV4dD48L3N2Zz4=';
                                        }}
                                      />
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center gap-3">
                                        <Eye className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <button
                                          data-no-print="true"
                                          onClick={(e) => { e.stopPropagation(); setEditingPhoto(photo); }}
                                          className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2 hover:bg-opacity-100 shadow-lg"
                                          title="Editar/Recortar foto"
                                        >
                                          <Edit3 className="w-4 h-4 text-gray-700" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Vídeo da Inspeção */}
          {(() => {
            const vidUrl = inspection.video_url_signed || inspection.video_url;
            if (!vidUrl) return null;
            const fullVidUrl = vidUrl.startsWith('http') ? vidUrl : `${window.location.origin}${vidUrl}`;
            const publicVidUrl = vidUrl.startsWith('http') ? vidUrl : `https://gestor.viploclocadora.com.br${vidUrl}`;
            return (
              <div data-no-break="true" data-no-break-buffer="30" className="rounded-xl border-2 border-orange-200 p-6 mb-6" style={{ backgroundColor: '#FFF8F0', overflow: 'hidden' }}>
                <h3
                  className="text-lg font-bold mb-4 flex items-center gap-2"
                  style={{ color: colors.navy }}
                >
                  <Film className="w-5 h-5" style={{ color: colors.orange }} />
                  Vídeo da Inspeção
                </h3>

                {/* Video player - visível na tela, oculto no PDF */}
                <div data-screen-only className="rounded-lg overflow-hidden border border-gray-300 bg-black">
                  <video
                    controls
                    className="w-full max-h-96"
                    preload="metadata"
                  >
                    <source src={fullVidUrl} type="video/mp4" />
                    Seu navegador não suporta a reprodução de vídeo.
                  </video>
                </div>

                <div className="mt-4 p-3 rounded-lg border border-orange-200 flex items-center gap-3" style={{ backgroundColor: '#FFF4E8' }} data-video-link="true">
                  <Film className="w-5 h-5 flex-shrink-0" style={{ color: colors.orange }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold" style={{ color: colors.navy }}>Link do Vídeo da Vistoria:</p>
                    {/* Tela: URL completa */}
                    <a
                      data-screen-only
                      href={publicVidUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm underline break-all"
                    >
                      {publicVidUrl}
                    </a>
                    {/* PDF: link clicável real via pdf.textWithLink() — não renderizado no canvas */}
                    <p data-pdf-link-text={publicVidUrl} style={{ display: 'none' }} className="text-blue-600 text-sm underline">
                      Toque aqui para assistir ao vídeo da vistoria
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Alertas */}
          {inspection.alerts && inspection.alerts.length > 0 && (
            <div data-no-break="true" className="rounded-xl border border-gray-200 p-6 mb-6">
              <h3
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <AlertCircle className="w-5 h-5" style={{ color: colors.warning }} />
                Alertas Gerados ({inspection.alerts.length})
              </h3>

              <div className="space-y-3">
                {inspection.alerts.map((alert, index) => (
                  <div
                    key={index}
                    className={`rounded-lg p-4 border-l-4 ${
                      alert.severity === 'critical' ? 'bg-red-50 border-red-500' :
                      alert.severity === 'high' ? 'bg-orange-50 border-orange-500' :
                      alert.severity === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                      'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                        alert.severity === 'critical' ? 'text-red-600' :
                        alert.severity === 'high' ? 'text-orange-600' :
                        alert.severity === 'medium' ? 'text-yellow-600' :
                        'text-blue-600'
                      }`} />
                      <div>
                        <p className="font-medium" style={{ color: colors.navy }}>
                          {alert.title || alert.message}
                        </p>
                        {alert.description && (
                          <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className={`px-2 py-1 rounded-full ${
                            alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            alert.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                            alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {alert.severity === 'critical' ? 'Crítico' :
                             alert.severity === 'high' ? 'Alto' :
                             alert.severity === 'medium' ? 'Médio' : 'Baixo'}
                          </span>
                          <span>{formatDateShort(alert.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Observações Gerais */}
          {inspection.notes && (
            <div data-no-break="true" className="rounded-xl border border-gray-200 p-6 mb-6">
              <h3
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <FileText className="w-5 h-5" style={{ color: colors.orange }} />
                Observações Gerais
              </h3>
              <p className="text-gray-700 whitespace-pre-wrap">{inspection.notes}</p>
            </div>
          )}

          {/* Observação Adicional para o Relatório */}
          <div data-no-break="true" data-no-break-buffer="15" className="rounded-xl border-2 border-indigo-200 p-6 mb-6">
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: colors.navy }}>
              <FileText className="w-5 h-5" style={{ color: colors.orange }} />
              Observação Adicional
            </h3>

            {/* Textarea — editável na tela, oculto no PDF */}
            <div data-no-print="true">
              <textarea
                value={reportObservation}
                onChange={e => setReportObservation(e.target.value)}
                placeholder="Informe aqui observações adicionais que serão incluídas no relatório em PDF enviado para assinatura..."
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 focus:outline-none focus:ring-2 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">Campo opcional — aparece no PDF após o relatório fotográfico</p>
            </div>

            {/* Texto formatado — visível apenas no PDF (data-pdf-only) */}
            {reportObservation ? (
              <p data-pdf-only style={{ display: 'none' }} className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{reportObservation}</p>
            ) : (
              <p data-pdf-only style={{ display: 'none' }} className="text-sm text-gray-400 italic">Sem observações adicionais</p>
            )}
          </div>

          {/* Entrega do Veículo - Condutor e Recebedor */}
          <div data-no-break="true" className="rounded-xl border-2 border-blue-200 p-6 mb-6" style={{ backgroundColor: '#F7F9FB', overflow: 'hidden' }}>
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-lg font-bold flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <Car className="w-5 h-5" style={{ color: colors.orange }} />
                Entrega do Veículo
              </h3>

              {/* Botão Editar - não aparece no PDF */}
              <div data-no-print="true">
                {!editingDelivery ? (
                  <button
                    onClick={() => {
                      setDeliveryForm({
                        condutor_nome: inspection.condutor_nome || inspection.driver_name || '',
                        condutor_cpf: inspection.condutor_cpf || '',
                      });
                      setEditingDelivery(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                    style={{ backgroundColor: colors.orange, color: 'white' }}
                  >
                    <Edit3 className="w-4 h-4" />
                    Editar
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDelivery}
                      disabled={savingDelivery}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-all hover:opacity-80 bg-green-600"
                    >
                      {savingDelivery ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Salvar
                    </button>
                    <button
                      onClick={() => setEditingDelivery(false)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 bg-gray-200 transition-all hover:bg-gray-300"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Vincular Contrato - apenas quando não tem contrato ainda */}
            {!inspection.contract_id && contracts.length > 0 && (
              <div data-no-print="true" className="mb-4">
                <label className="text-sm text-gray-500 mb-1 block">Vincular Contrato</label>
                <select
                  value={selectedContractId}
                  onChange={(e) => handleContractSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  style={{ color: colors.navy }}
                >
                  <option value="">-- Selecionar contrato --</option>
                  {contracts.map(c => (
                    <option key={c.id} value={c.id}>
                      Contrato #{c.contract_identifier || c.contract_number} - {c.client_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Dados do Condutor */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Condutor</p>
              <div className={`grid gap-4 ${inspection.face_verification_photo ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <div className="rounded-lg border border-gray-200 p-4" style={{ backgroundColor: '#F0F4F8' }}>
                  <p className="text-sm text-gray-500 mb-1">Nome</p>
                  {editingDelivery ? (
                    <input
                      type="text"
                      value={deliveryForm.condutor_nome}
                      onChange={(e) => setDeliveryForm(prev => ({ ...prev, condutor_nome: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-semibold focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      placeholder="Nome completo do condutor"
                      style={{ color: colors.navy }}
                    />
                  ) : (
                    <p className="font-semibold" style={{ color: colors.navy }}>
                      {inspection.condutor_nome || inspection.driver_name || 'Não informado'}
                    </p>
                  )}
                </div>
                <div className="rounded-lg border border-gray-200 p-4" style={{ backgroundColor: '#F0F4F8' }}>
                  <p className="text-sm text-gray-500 mb-1">CPF</p>
                  {editingDelivery ? (
                    <input
                      type="text"
                      value={deliveryForm.condutor_cpf}
                      onChange={(e) => setDeliveryForm(prev => ({ ...prev, condutor_cpf: formatCpf(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-semibold focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      placeholder="000.000.000-00"
                      style={{ color: colors.navy }}
                    />
                  ) : (
                    <p className="font-semibold" style={{ color: colors.navy }}>
                      {inspection.condutor_cpf || 'Não informado'}
                    </p>
                  )}
                </div>
                {/* Foto do Condutor (verificação facial do app) */}
                {inspection.face_verification_photo && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden" style={{ backgroundColor: '#F0F4F8' }}>
                    <p className="text-sm text-gray-500 px-3 pt-3 pb-1">Foto do Condutor</p>
                    <img
                      src={(() => {
                        const p = inspection.face_verification_photo;
                        if (p.startsWith('/uploads/')) return `/api${p}`;
                        if (p.startsWith('uploads/')) return `/api/${p}`;
                        return p;
                      })()}
                      alt="Foto do Condutor"
                      className="w-full object-cover"
                      style={{ height: '140px' }}
                    />
                  </div>
                )}
              </div>

              {/* Dados adicionais do Termo de Autorização */}
              {inspection.authorization_term && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  {inspection.authorization_term.representative_rg && (
                    <div className="rounded-lg border border-gray-200 p-3" style={{ backgroundColor: '#F0F4F8' }}>
                      <p className="text-xs text-gray-500 mb-0.5">RG</p>
                      <p className="font-semibold text-sm" style={{ color: colors.navy }}>{inspection.authorization_term.representative_rg}</p>
                    </div>
                  )}
                  {inspection.authorization_term.representative_cnh && (
                    <div className="rounded-lg border border-gray-200 p-3" style={{ backgroundColor: '#F0F4F8' }}>
                      <p className="text-xs text-gray-500 mb-0.5">CNH</p>
                      <p className="font-semibold text-sm" style={{ color: colors.navy }}>{inspection.authorization_term.representative_cnh}</p>
                      {inspection.authorization_term.representative_cnh_category && (
                        <p className="text-xs text-gray-400">Cat: {inspection.authorization_term.representative_cnh_category}</p>
                      )}
                    </div>
                  )}
                  {inspection.authorization_term.representative_phone && (
                    <div className="rounded-lg border border-gray-200 p-3" style={{ backgroundColor: '#F0F4F8' }}>
                      <p className="text-xs text-gray-500 mb-0.5">Telefone</p>
                      <p className="font-semibold text-sm" style={{ color: colors.navy }}>{inspection.authorization_term.representative_phone}</p>
                    </div>
                  )}
                  {inspection.authorization_term.representative_role && (
                    <div className="rounded-lg border border-gray-200 p-3" style={{ backgroundColor: '#F0F4F8' }}>
                      <p className="text-xs text-gray-500 mb-0.5">Função</p>
                      <p className="font-semibold text-sm" style={{ color: colors.navy }}>{inspection.authorization_term.representative_role}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Geolocalização da Inspeção (para validade jurídica no PDF) */}
            {(inspection.latitude && inspection.longitude) && (
              <div className="mb-6 rounded-xl border border-green-200 p-4" style={{ backgroundColor: '#f0fdf4' }}>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Geolocalização da Inspeção</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Latitude</p>
                    <p className="font-semibold text-sm" style={{ color: colors.navy }}>{Number(inspection.latitude).toFixed(7)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Longitude</p>
                    <p className="font-semibold text-sm" style={{ color: colors.navy }}>{Number(inspection.longitude).toFixed(7)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Data/Hora da Execução</p>
                    <p className="font-semibold text-sm" style={{ color: colors.navy }}>
                      {inspection.executed_at ? new Date(inspection.executed_at).toLocaleString('pt-BR') : 'N/A'}
                    </p>
                  </div>
                  <div data-no-print="true">
                    <p className="text-xs text-gray-500">Mapa</p>
                    <a
                      href={`https://www.google.com/maps?q=${inspection.latitude},${inspection.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline font-medium"
                    >
                      Abrir no Google Maps
                    </a>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2 italic">Coordenadas GPS capturadas no momento da inspeção via dispositivo móvel</p>
              </div>
            )}

            {/* Dados do Recebedor (preenchido pelo app mobile) */}
            {(inspection.recebedor_nome || inspection.recebedor_foto || inspection.signature_recebedor || inspection.signature_condutor) && (
              <div className="mb-6 pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Recebimento do Veículo</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {inspection.recebedor_nome && (
                    <div className="rounded-lg border border-gray-200 p-4" style={{ backgroundColor: '#F0F4F8' }}>
                      <p className="text-sm text-gray-500 mb-1">Recebedor</p>
                      <p className="font-semibold" style={{ color: colors.navy }}>{inspection.recebedor_nome}</p>
                    </div>
                  )}
                  {inspection.recebedor_cpf && (
                    <div className="rounded-lg border border-gray-200 p-4" style={{ backgroundColor: '#F0F4F8' }}>
                      <p className="text-sm text-gray-500 mb-1">CPF do Recebedor</p>
                      <p className="font-semibold" style={{ color: colors.navy }}>{inspection.recebedor_cpf}</p>
                    </div>
                  )}
                </div>
                {/* Foto e Assinaturas digitais do app */}
                <div className="grid grid-cols-3 gap-4">
                  {inspection.recebedor_foto && (
                    <div className="text-center">
                      <div className="border-2 border-gray-200 rounded-lg overflow-hidden mb-2">
                        <img
                          src={(() => {
                            const p = inspection.recebedor_foto;
                            if (!p) return '';
                            if (p.startsWith('/uploads/')) return `/api${p}`;
                            if (p.startsWith('uploads/')) return `/api/${p}`;
                            return p;
                          })()}
                          alt="Foto do Recebedor"
                          className="w-full object-cover"
                          style={{ height: '140px' }}
                        />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Foto do Recebedor</p>
                    </div>
                  )}
                  {inspection.signature_condutor && (
                    <div className="text-center">
                      <div className="border-2 border-gray-200 rounded-lg p-3 bg-white mb-2">
                        <img src={inspection.signature_condutor} alt="Assinatura do Condutor" className="max-h-24 mx-auto" />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Assinatura do Condutor</p>
                      <p className="text-xs text-gray-500">{inspection.condutor_nome || inspection.driver_name || ''}</p>
                    </div>
                  )}
                  {inspection.signature_recebedor && (
                    <div className="text-center">
                      <div className="border-2 border-gray-200 rounded-lg p-3 bg-white mb-2">
                        <img src={inspection.signature_recebedor} alt="Assinatura do Recebedor" className="max-h-24 mx-auto" />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Assinatura do Recebedor</p>
                      <p className="text-xs text-gray-500">{inspection.recebedor_nome || ''}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assinaturas - Partes do Contrato */}
            <div className="pt-4 border-t border-gray-200">
              {contractParties ? (
                <>
                  {/* Locatários */}
                  {contractParties.locatarios.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Locatário(s)</p>
                      <div className="grid grid-cols-2 gap-4">
                        {contractParties.locatarios.map((p, i) => (
                          <div key={i} className="text-center">
                            <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 mb-2 min-h-[80px] flex items-center justify-center">
                              <p className="text-gray-400 text-sm">Assinatura pendente</p>
                            </div>
                            <div className="border-t-2 border-gray-400 mx-4 pt-2">
                              <p className="text-sm font-medium" style={{ color: colors.navy }}>{p.name || '___________________'}</p>
                              {p.cpf && <p className="text-xs text-gray-500">CPF: {p.cpf}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Locadores */}
                  {contractParties.locadores.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Locadora (VIPLOC)</p>
                      <div className="grid grid-cols-2 gap-4">
                        {contractParties.locadores.map((p, i) => (
                          <div key={i} className="text-center">
                            <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 mb-2 min-h-[80px] flex items-center justify-center">
                              <p className="text-gray-400 text-sm">Assinatura pendente</p>
                            </div>
                            <div className="border-t-2 border-gray-400 mx-4 pt-2">
                              <p className="text-sm font-medium" style={{ color: colors.navy }}>{p.name || '___________________'}</p>
                              {p.cpf && <p className="text-xs text-gray-500">CPF: {p.cpf}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">Nenhum contrato vinculado — signatários não disponíveis</p>
              )}
            </div>

            {/* Declaração de recebimento */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700 text-center italic">
                Declaro que recebi o veículo <strong>{inspection.license_plate || inspection.vehicle_plate || inspection.vehicle_license_plate || 'N/A'}</strong> ({inspection.vehicle_model || ''})
                nas condições descritas neste relatório de inspeção, tendo verificado todos os itens acima.
                {inspection.odometer_reading && (
                  <> Quilometragem: <strong>{Number(inspection.odometer_reading).toLocaleString('pt-BR')} km</strong>.</>
                )}
              </p>
              <p className="text-xs text-gray-500 text-center mt-2">
                Data: {formatDate(inspection.completed_at || inspection.executed_at || inspection.created_at)}
              </p>
            </div>
          </div>

          {/* Modal de Seleção de Signatários */}
          {showSignerSelection && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b">
                  <h2 className="text-lg font-bold" style={{ color: '#2B3E5C' }}>
                    Selecionar Signatários
                  </h2>
                  <button onClick={() => setShowSignerSelection(false)} className="p-1 rounded-lg hover:bg-gray-100">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  {loadingSigners ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader className="w-6 h-6 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Carregando signatários...</span>
                    </div>
                  ) : (
                    <>
                      {/* Locatários */}
                      {availableSigners.locatarios.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Locatários / Cliente</p>
                          <div className="space-y-2">
                            {availableSigners.locatarios.map((signer, i) => {
                              const checked = selectedSigners.some(s => s.email === signer.email);
                              return (
                                <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleSigner(signer)} className="mt-0.5 accent-blue-600" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm" style={{ color: '#2B3E5C' }}>{signer.name || '(sem nome)'}</p>
                                    <p className="text-xs text-gray-500">{signer.email || '(sem email)'}</p>
                                    {signer.cpf && <p className="text-xs text-gray-400">CPF: {signer.cpf}</p>}
                                    <span className="inline-block text-xs bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 mt-1">{signer.source}</span>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Sócios / Locadores */}
                      {availableSigners.locadores.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sócios VIPLOC / Locadora</p>
                          <div className="space-y-2">
                            {availableSigners.locadores.map((signer, i) => {
                              const checked = selectedSigners.some(s => s.email === signer.email);
                              return (
                                <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleSigner(signer)} className="mt-0.5 accent-orange-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm" style={{ color: '#2B3E5C' }}>{signer.name}</p>
                                    <p className="text-xs text-gray-500">{signer.email}</p>
                                    {signer.cpf && <p className="text-xs text-gray-400">CPF: {signer.cpf}</p>}
                                    <div className="flex items-center gap-1 mt-1">
                                      <span className="inline-block text-xs bg-orange-100 text-orange-700 rounded px-1.5 py-0.5">{signer.source}</span>
                                      {signer.primary && <span className="inline-block text-xs bg-green-100 text-green-700 rounded px-1.5 py-0.5">Principal</span>}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Signatários personalizados já adicionados */}
                      {selectedSigners.filter(s => s.source === 'Personalizado').length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Adicionados Manualmente</p>
                          <div className="space-y-2">
                            {selectedSigners.filter(s => s.source === 'Personalizado').map((signer, i) => (
                              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 bg-purple-50">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm" style={{ color: '#2B3E5C' }}>{signer.name}</p>
                                  <p className="text-xs text-gray-500">{signer.email}</p>
                                  {signer.cpf && <p className="text-xs text-gray-400">CPF: {signer.cpf}</p>}
                                  <span className="inline-block text-xs bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 mt-1">{signer.role === 'locador' ? 'Locadora' : 'Locatário'}</span>
                                </div>
                                <button onClick={() => setSelectedSigners(prev => prev.filter(s => s.email !== signer.email))} className="text-red-400 hover:text-red-600 mt-0.5">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Adicionar personalizado */}
                      {showAddCustom ? (
                        <div className="border border-dashed border-purple-300 rounded-lg p-3 space-y-2 bg-purple-50">
                          <p className="text-xs font-semibold text-purple-700 mb-1">Novo Signatário</p>
                          <input
                            type="text" placeholder="Nome completo*"
                            value={customSigner.name}
                            onChange={e => setCustomSigner(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                          />
                          <input
                            type="email" placeholder="Email*"
                            value={customSigner.email}
                            onChange={e => setCustomSigner(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                          />
                          <input
                            type="text" placeholder="CPF (opcional)"
                            value={customSigner.cpf}
                            onChange={e => setCustomSigner(prev => ({ ...prev, cpf: formatCpf(e.target.value) }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                          />
                          <select
                            value={customSigner.role}
                            onChange={e => setCustomSigner(prev => ({ ...prev, role: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                          >
                            <option value="locatario">Locatário</option>
                            <option value="locador">Locadora / VIPLOC</option>
                          </select>
                          <div className="flex gap-2">
                            <button onClick={handleAddCustomSigner} className="flex-1 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors">Adicionar</button>
                            <button onClick={() => setShowAddCustom(false)} className="flex-1 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors">Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAddCustom(true)}
                          className="w-full py-2 text-sm rounded-lg border border-dashed border-purple-400 text-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center gap-1"
                        >
                          + Adicionar Signatário Personalizado
                        </button>
                      )}

                      {/* Resumo e confirmar */}
                      <div className="pt-2 border-t">
                        <p className="text-sm text-gray-600 mb-3">
                          {selectedSigners.length === 0
                            ? 'Nenhum signatário selecionado'
                            : `${selectedSigners.length} signatário(s) selecionado(s)`}
                        </p>
                        <button
                          onClick={handleSendToClicksign}
                          disabled={selectedSigners.length === 0 || sendingToClicksign}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: '#2B3E5C' }}
                        >
                          {sendingToClicksign ? (
                            <><Loader className="w-5 h-5 animate-spin" /> Gerando PDF e enviando...</>
                          ) : (
                            <><Send className="w-5 h-5" /> Confirmar e Enviar ({selectedSigners.length})</>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Botão ClickSign + Status - não aparece no PDF */}
          <div data-no-print="true" className="mb-6">
            {/* Botão Enviar para ClickSign */}
            {!inspection.clicksign_status && inspection.status === 'completed' && (
              <button
                onClick={handleOpenSignerSelection}
                disabled={sendingToClicksign}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-white transition-all hover:opacity-90"
                style={{ backgroundColor: '#2B3E5C' }}
              >
                {sendingToClicksign ? (
                  <><Loader className="w-5 h-5 animate-spin" /> Enviando para ClickSign...</>
                ) : (
                  <><Send className="w-5 h-5" /> Enviar para Assinatura Digital (ClickSign)</>
                )}
              </button>
            )}

            {/* Status ClickSign */}
            {inspection.clicksign_status === 'running' && (
              <div className="rounded-xl border-2 border-yellow-300 p-4" style={{ backgroundColor: '#FFFBEB' }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <span className="font-bold text-yellow-700">Aguardando Assinaturas Digitais</span>
                  </div>
                  <button
                    onClick={handleSyncSignatures}
                    disabled={syncingSignatures}
                    title="Atualizar status das assinaturas"
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-yellow-400 text-yellow-700 hover:bg-yellow-100 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${syncingSignatures ? 'animate-spin' : ''}`} />
                    {syncingSignatures ? 'Atualizando...' : 'Atualizar'}
                  </button>
                </div>

                {signatures.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {signatures.map((sig) => (
                      <div key={sig.id} className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-200">
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#2B3E5C' }}>{sig.signer_name}</p>
                          <p className="text-xs text-gray-500">{sig.signer_email} · {sig.signer_role === 'locador' ? 'Locadora' : 'Locatário'}</p>
                          {sig.signed && sig.signed_at && (
                            <p className="text-xs text-green-600 mt-0.5">Assinado em: {new Date(sig.signed_at).toLocaleString('pt-BR')}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {sig.signed ? (
                            <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                              <CheckCircle className="w-4 h-4" /> Assinado
                            </span>
                          ) : (
                            <>
                              <span className="flex items-center gap-1 text-xs font-medium text-yellow-600">
                                <Clock className="w-4 h-4" /> Pendente
                              </span>
                              <button
                                onClick={() => handleResendSigner(sig)}
                                disabled={resendingSignerId === sig.id}
                                title={`Reenviar lembrete para ${sig.signer_name}`}
                                className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                              >
                                {resendingSignerId === sig.id ? (
                                  <Loader className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Bell className="w-3 h-3" />
                                )}
                                Lembrete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={handleCancelClicksign}
                  disabled={cancellingClicksign}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {cancellingClicksign ? (
                    <><Loader className="w-4 h-4 animate-spin" /> Cancelando...</>
                  ) : (
                    <><Trash2 className="w-4 h-4" /> Cancelar e Enviar Novo Documento</>
                  )}
                </button>
              </div>
            )}

            {inspection.clicksign_status === 'closed' && (
              <div className="rounded-xl border-2 border-green-300 p-4" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="font-bold text-green-700">Assinado Digitalmente via ClickSign</span>
                </div>
              </div>
            )}
          </div>

          {/* Assinaturas Digitais ClickSign - aparece no relatório/PDF quando assinadas */}
          {signatures.length > 0 && signatures.some(s => s.signed) && (
            <div data-no-break="true" className="rounded-xl border-2 border-green-200 p-6 mb-6" style={{ backgroundColor: '#F0FDF4' }}>
              <h3
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <Shield className="w-5 h-5 text-green-600" />
                Assinaturas Digitais (ClickSign)
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {signatures.filter(s => s.signed).map((sig, idx) => (
                  <div key={idx} className="rounded-lg border border-green-200 p-4" style={{ backgroundColor: '#ECFDF5' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-bold" style={{ color: colors.navy }}>
                        {sig.signer_role === 'locador' ? 'Locadora / VIPLOC' : 'Locatário'}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: colors.navy }}><strong>Nome:</strong> {sig.signer_name}</p>
                    <p className="text-sm text-gray-600"><strong>Email:</strong> {sig.signer_email}</p>
                    {sig.signer_cpf && <p className="text-sm text-gray-600"><strong>CPF:</strong> {sig.signer_cpf}</p>}
                    <p className="text-sm text-green-700 mt-1">
                      <strong>Assinado em:</strong> {formatDate(sig.signed_at)}
                    </p>
                  </div>
                ))}
              </div>

              {inspection.clicksign_document_key && (
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Documento ClickSign: {inspection.clicksign_document_key} | Status: {inspection.clicksign_status === 'closed' ? 'Concluído' : 'Em andamento'}
                </p>
              )}
            </div>
          )}

          {/* Assinaturas Adicionais (Motorista/Mecânico/Cliente) */}
          {(inspection.signature_driver || inspection.signature_mechanic || inspection.signature_client) && (
            <div data-no-break="true" className="rounded-xl border border-gray-200 p-6 mb-6">
              <h3
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: colors.navy }}
              >
                <User className="w-5 h-5" style={{ color: colors.orange }} />
                Assinaturas da Inspeção
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {inspection.signature_driver && (
                  <div className="text-center">
                    <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 mb-2">
                      <img
                        src={inspection.signature_driver}
                        alt="Assinatura do Motorista"
                        className="max-h-24 mx-auto"
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-600">Motorista</p>
                    <p className="text-sm text-gray-500">{inspection.driver_name}</p>
                  </div>
                )}

                {inspection.signature_mechanic && (
                  <div className="text-center">
                    <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 mb-2">
                      <img
                        src={inspection.signature_mechanic}
                        alt="Assinatura do Mecânico"
                        className="max-h-24 mx-auto"
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-600">Mecânico/Inspetor</p>
                  </div>
                )}

                {inspection.signature_client && (
                  <div className="text-center">
                    <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 mb-2">
                      <img
                        src={inspection.signature_client}
                        alt="Assinatura do Cliente"
                        className="max-h-24 mx-auto"
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-600">Cliente</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rodapé */}
          <div
            data-no-break="true"
            className="rounded-xl p-4 text-center"
            style={{ backgroundColor: colors.navy }}
          >
            <p className="text-white text-sm">
              VIPLOC - Gestão de Locação de Veículos | Relatório gerado automaticamente
            </p>
            <p className="text-gray-400 text-xs mt-1">
              {formatDate(new Date())} | www.viploclocadora.com.br
            </p>
          </div>
        </div>
      </div>

      {/* Modal de Foto Ampliada */}
      {/* Modal Editor de Foto */}
      {editingPhoto && (
        <PhotoEditorModal
          photo={editingPhoto}
          inspectionId={inspection.id}
          onSave={handlePhotoUpdated}
          onClose={() => setEditingPhoto(null)}
        />
      )}

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-60 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={getPhotoSrc(selectedPhoto)}
            alt={selectedPhoto.description || 'Foto da inspeção'}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
          {selectedPhoto.description && (
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <p className="text-white bg-black bg-opacity-50 rounded-lg px-4 py-2 inline-block">
                {selectedPhoto.description}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InspectionReportModal;
